import React, { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  Copy,
  Briefcase,
  FileText,
  Sparkles,
  Loader2,
  TrendingUp,
  AlertCircle,
  Upload,
  Lock,
  LogOut,
  User
} from 'lucide-react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const handleApiResponse = async (res, defaultError = 'Request failed') => {
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || defaultError);
    }
    return json;
  }
  const text = await res.text();
  let errorMsg = defaultError;
  if (res.status === 404) {
    errorMsg = `API endpoint not found (404). If running locally, please start the project using 'vercel dev' instead of 'npm start' to enable backend API routes.`;
  } else if (text.trim().startsWith('<')) {
    errorMsg = `Invalid server response (${res.status}). If running locally, make sure to run the project using 'vercel dev' so that the serverless functions in the /api directory are served.`;
  } else {
    errorMsg = `Server error (${res.status}): ${text.substring(0, 100) || 'No details available'}`;
  }
  throw new Error(errorMsg);
};

const plans = [
  { id: 'starter', name: 'Starter', price: 9, credits: 20, stripeUrl: 'https://buy.stripe.com/00w9AL3TgcBn04h83i8g004' },
  { id: 'pro', name: 'Pro', price: 19, credits: 100, stripeUrl: 'https://buy.stripe.com/cNifZ90H48l718l1EU8g003', recommended: true }
];

const PaywallModal = ({ onClose, onPaymentSuccess, onServerVerifyPaypal, initialPlanId, userId, userEmail }) => {
  const [method, setMethod] = useState(null); // 'stripe' or 'paypal'
  const [selectedPlan, setSelectedPlan] = useState(null); // { id: 'starter', price: 9, credits: 20 }

  useEffect(() => {
    if (initialPlanId) {
      const plan = plans.find(p => p.id === initialPlanId);
      if (plan) setSelectedPlan(plan);
    }
  }, [initialPlanId]);

  useEffect(() => {
    if (method === 'paypal' && selectedPlan) {
      // Clear previous buttons
      const container = document.getElementById('paypal-button-container');
      if (container) container.innerHTML = '';

      window.paypal.Buttons({
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              amount: { value: selectedPlan.price.toString() },
              description: `${selectedPlan.credits} CV Matcher Credits`
            }]
          });
        },
        onApprove: async (data, actions) => {
          await actions.order.capture();
          onServerVerifyPaypal(data.orderID);
        },
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'paypal'
        }
      }).render('#paypal-button-container');
    }
  }, [method, selectedPlan, onPaymentSuccess, onServerVerifyPaypal]);

  return (
    <div className="flex flex-col items-center p-6 md:p-8 text-center bg-gray-900/95 backdrop-blur-xl border border-indigo-500/30 rounded-3xl shadow-2xl max-w-2xl w-full mx-auto relative max-h-[90vh] overflow-y-auto">
      {onClose && (
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
          <XCircle className="w-8 h-8" />
        </button>
      )}
      
      <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 flex items-center justify-center mb-6 mt-4">
        <Sparkles className="w-8 h-8 text-indigo-400" />
      </div>
      
      <h3 className="text-3xl font-bold mb-2 text-white">You're 1 optimization away from more interviews</h3>
      <p className="text-indigo-200/70 mb-8 text-lg">
        Choose a plan to continue optimizing your professional profile.
      </p>

      {!selectedPlan ? (
        <div className="grid sm:grid-cols-2 gap-6 w-full mb-6">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              onClick={() => setSelectedPlan(plan)}
              className={`p-6 rounded-2xl border transition-all cursor-pointer text-left flex flex-col relative ${
                plan.recommended 
                  ? 'bg-indigo-600/10 border-indigo-500 shadow-[0_0_20px_-5px_rgba(99,102,241,0.3)]' 
                  : 'bg-gray-950 border-gray-800 hover:border-gray-700'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-6 bg-indigo-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                  Best Value
                </div>
              )}
              <h4 className="text-xl font-bold text-white mb-1">{plan.name}</h4>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">${plan.price}</span>
                <span className="text-gray-400 text-sm"> / {plan.credits} credits</span>
              </div>
              <ul className="space-y-3 mb-6 text-sm text-gray-400">
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-indigo-400 mr-2 shrink-0" /> {plan.credits} analyses</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-indigo-400 mr-2 shrink-0" /> Full CV rewrites</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 text-indigo-400 mr-2 shrink-0" /> Priority AI processing</li>
              </ul>
              <button className={`w-full py-3 rounded-xl font-bold transition-all ${
                plan.recommended ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-300'
              }`}>
                Get {plan.credits} analyses — ${plan.price}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-300">
          <button 
            onClick={() => { setSelectedPlan(null); setMethod(null); }}
            className="mb-6 text-sm text-gray-400 hover:text-white flex items-center transition-colors"
          >
            ← Back to plans
          </button>
          
          <div className="bg-gray-950/50 border border-gray-800 rounded-2xl p-6 mb-8 text-left">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-bold text-white text-lg">{selectedPlan.name} Plan</h4>
                <p className="text-gray-400 text-sm">{selectedPlan.credits} credits for your account</p>
              </div>
              <div className="text-2xl font-bold text-white">${selectedPlan.price}</div>
            </div>
          </div>

          <div className="grid gap-4">
            {/* Stripe Button */}
            <a 
              href={`${selectedPlan.stripeUrl}?client_reference_id=${encodeURIComponent(userId || '')}&prefilled_email=${encodeURIComponent(userEmail || '')}`}
              className="w-full py-4 bg-[#635BFF] hover:bg-[#5851E0] text-white font-bold rounded-xl transition-all flex items-center justify-center shadow-lg"
            >
              <div className="flex items-center">
                <span>Pay with Card / Apple Pay</span>
              </div>
            </a>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-800"></div></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-[#0B0F19] px-2 text-gray-500">Or</span></div>
            </div>

            {/* PayPal Button Container */}
            <div className="w-full" onClick={() => setMethod('paypal')}>
              {!method && (
                <button className="w-full py-4 bg-[#FFC439] hover:bg-[#F2BA36] text-[#003087] font-bold rounded-xl transition-all flex items-center justify-center shadow-lg">
                  Pay with PayPal
                </button>
              )}
              <div id="paypal-button-container" className="w-full min-h-[50px]"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { number: 1, title: 'Upload CV', description: 'PDF or text' },
    { number: 2, title: 'Job Info', description: 'Paste description' },
    { number: 3, title: 'Get Results', description: 'Match & fixes' }
  ];

  return (
    <div className="max-w-3xl mx-auto mb-12">
      <div className="flex items-center justify-between relative">
        {/* Connector Line */}
        <div className="absolute top-5 left-0 w-full h-0.5 bg-gray-800 -z-10" />
        <div 
          className="absolute top-5 left-0 h-0.5 bg-indigo-500 transition-all duration-500 -z-10" 
          style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
        />
        
        {steps.map((step) => (
          <div key={step.number} className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
              currentStep >= step.number 
                ? 'bg-indigo-600 border-indigo-500 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)]' 
                : 'bg-gray-900 border-gray-800 text-gray-500'
            }`}>
              {currentStep > step.number ? <CheckCircle2 className="w-6 h-6" /> : <span className="font-bold">{step.number}</span>}
            </div>
            <div className="mt-3 text-center">
              <div className={`text-sm font-bold ${currentStep >= step.number ? 'text-white' : 'text-gray-500'}`}>{step.title}</div>
              <div className="text-[11px] text-gray-500 hidden sm:block uppercase tracking-wider mt-0.5">{step.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Matcher = ({ session }) => {
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const [initialPlan, setInitialPlan] = useState(null);
  const [cvText, setCvText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedFullCV, setCopiedFullCV] = useState(false);

  const [credits, setCredits] = useState(session ? 0 : 1);
  const [isUnlockingPremium, setIsUnlockingPremium] = useState(false);
  const [premiumUnlocked, setPremiumUnlocked] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [simulationStep, setSimulationStep] = useState('');
  const [showSignupTeaser, setShowSignupTeaser] = useState(false);

  useEffect(() => {
    const syncCredits = async () => {
      if (!session?.user) return;

      try {
        let { data, error: fetchError } = await supabase
          .from('profiles')
          .select('credits')
          .eq('id', session.user.id)
          .single();

        if (fetchError && fetchError.code === 'PGRST116') {
          const { data: newData, error: createError } = await supabase
            .from('profiles')
            .insert([{ id: session.user.id, email: session.user.email, credits: 3 }])
            .select()
            .single();
          
          if (createError) throw createError;
          data = newData;
        } else if (fetchError) {
          throw fetchError;
        }

        setCredits(data.credits);

        const params = new URLSearchParams(window.location.search);
        if (params.get('payment') === 'success') {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        if (params.get('plan')) {
          setInitialPlan(params.get('plan'));
          setShowLimitModal(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Auto-trigger analysis if returning from login
        const savedCv = sessionStorage.getItem('pending_cv_text');
        const savedJob = sessionStorage.getItem('pending_job_desc');
        const triggerReal = sessionStorage.getItem('pending_trigger_analysis');

        if (savedCv && savedJob && triggerReal === 'true') {
          sessionStorage.removeItem('pending_trigger_analysis');
          
          setLoading(true);
          setError(null);
          setResults(null);
          setPremiumUnlocked(false);
          setShowLimitModal(false);

          const token = session.access_token;
          const res = await fetch('/api/analyze', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ cvText: savedCv, jobDescription: savedJob })
          });

          const json = await handleApiResponse(res, 'Analysis failed');
          setResults(json.data);
          if (json.credits !== undefined) setCredits(json.credits);
        }
      } catch (err) {
        console.error("Error syncing credits or auto-analyzing:", err);
        setError(err.message || "An error occurred during analysis.");
      } finally {
        setLoading(false);
      }
    };

    syncCredits();
  }, [session]);

  useEffect(() => {
    const savedCv = sessionStorage.getItem('pending_cv_text');
    const savedJob = sessionStorage.getItem('pending_job_desc');
    if (savedCv) setCvText(savedCv);
    if (savedJob) setJobDescription(savedJob);
  }, []);

  useEffect(() => {
    if (cvText) {
      sessionStorage.setItem('pending_cv_text', cvText);
    } else {
      sessionStorage.removeItem('pending_cv_text');
    }
  }, [cvText]);

  useEffect(() => {
    if (jobDescription) {
      sessionStorage.setItem('pending_job_desc', jobDescription);
    } else {
      sessionStorage.removeItem('pending_job_desc');
    }
  }, [jobDescription]);

  useEffect(() => {
    if (results && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [results]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Please upload a valid PDF file.');
      return;
    }

    setIsPdfLoading(true);
    setError(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += content.items.map(item => item.str).join(' ') + '\n';
      }
      setCvText(text);
    } catch (err) {
      console.error(err);
      setError('Error reading PDF file. Please try pasting the text manually.');
    } finally {
      setIsPdfLoading(false);
      e.target.value = null;
    }
  };

  const getAuthToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token;
  };

  const handleAnalyze = async () => {
    if (!cvText.trim() || !jobDescription.trim()) {
      setError("Please provide both CV and Job Description.");
      return;
    }

    if (!session) {
      setLoading(true);
      setError(null);
      setResults(null);
      setShowSignupTeaser(false);
      setSimulationStep("Parsing resume structure...");
      
      setTimeout(() => {
        setSimulationStep("Extracting keywords & requirements...");
        setTimeout(() => {
          setSimulationStep("Calculating ATS match score...");
          setTimeout(() => {
            setLoading(false);
            setShowSignupTeaser(true);
            sessionStorage.setItem('pending_cv_text', cvText);
            sessionStorage.setItem('pending_job_desc', jobDescription);
            sessionStorage.setItem('pending_trigger_analysis', 'true');
          }, 1200);
        }, 1200);
      }, 1000);
      return;
    }

    if (Number(credits) <= 0) {
      setShowLimitModal(true);
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setPremiumUnlocked(false);
    setShowLimitModal(false);

    try {
      const token = await getAuthToken();
      const res = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ cvText, jobDescription })
      });

      const json = await handleApiResponse(res, 'Analysis failed');
      setResults(json.data);
      if (json.credits !== undefined) setCredits(json.credits);
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred during analysis.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockPremium = async () => {
    if (Number(credits) <= 0) {
      setShowLimitModal(true);
      return;
    }

    setIsUnlockingPremium(true);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/unlock-premium', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const json = await handleApiResponse(res, 'Failed to unlock');
      setCredits(json.credits);
      setPremiumUnlocked(true);
    } catch (err) {
      console.error(err);
      setError("Failed to unlock premium analysis.");
    } finally {
      setIsUnlockingPremium(false);
    }
  };


  const handlePaymentSuccess = async (newTotal) => {
    setCredits(newTotal);
    setShowLimitModal(false);
  };

  const handleServerVerifyPaypal = async (orderID) => {
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/paypal-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orderID })
      });
      const json = await handleApiResponse(res, 'Payment verification failed');
      handlePaymentSuccess(json.credits);
    } catch (err) {
      console.error('PayPal verification error:', err);
      setError('Payment verification failed. Please contact support.');
    }
  };

  const handleCopy = () => {
    if (results?.rewritten_summary) {
      navigator.clipboard.writeText(results.rewritten_summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyFullCV = () => {
    if (results?.optimized_cv) {
      navigator.clipboard.writeText(results.optimized_cv);
      setCopiedFullCV(true);
      setTimeout(() => setCopiedFullCV(false), 2000);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-400 stroke-emerald-400';
    if (score >= 50) return 'text-yellow-400 stroke-yellow-400';
    return 'text-rose-400 stroke-rose-400';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-emerald-500/10 border-emerald-500/20';
    if (score >= 50) return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-rose-500/10 border-rose-500/20';
  };

  const renderMarkdown = (text, keywords = []) => {
    if (!text) return '';
    
    // Escape keywords for regex matching
    const escapedKeywords = keywords
      .map(k => k.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'))
      .filter(k => k.length > 0);
    
    const hasKeywords = escapedKeywords.length > 0;
    const keywordRegex = hasKeywords ? new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'gi') : null;

    // Helper to render bold text and keywords inline
    const renderInline = (inputText) => {
      if (!inputText) return '';
      
      const boldParts = inputText.split(/(\*\*[^*]+\*\*)/g);
      
      return boldParts.map((part, bIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          const rawText = part.slice(2, -2);
          if (keywordRegex) {
            const inlineParts = rawText.split(keywordRegex);
            return (
              <strong key={bIdx} className="font-bold text-white">
                {inlineParts.map((subPart, sIdx) => 
                  keywordRegex.test(subPart) ? (
                    <mark key={sIdx} className="bg-emerald-500/20 text-emerald-300 px-1 py-0.5 rounded border border-emerald-500/30">
                      {subPart}
                    </mark>
                  ) : subPart
                )}
              </strong>
            );
          }
          return <strong key={bIdx} className="font-bold text-white">{rawText}</strong>;
        }
        
        if (keywordRegex) {
          const inlineParts = part.split(keywordRegex);
          return inlineParts.map((subPart, sIdx) => 
            keywordRegex.test(subPart) ? (
              <mark key={`${bIdx}-${sIdx}`} className="bg-emerald-500/20 text-emerald-300 px-1 py-0.5 rounded border border-emerald-500/30">
                {subPart}
              </mark>
            ) : subPart
          );
        }
        
        return part;
      });
    };

    const lines = text.split('\n');
    
    return lines.map((line, lineIdx) => {
      const trimmed = line.trim();
      
      if (trimmed === '---') {
        return <hr key={lineIdx} className="my-4 border-gray-800" />;
      }
      
      if (trimmed.startsWith('### ')) {
        return (
          <h3 key={lineIdx} className="text-base font-bold text-indigo-400 mt-5 mb-2 uppercase tracking-wide">
            {renderInline(trimmed.slice(4))}
          </h3>
        );
      }
      if (trimmed.startsWith('## ')) {
        return (
          <h2 key={lineIdx} className="text-lg font-bold text-white mt-6 mb-3 border-b border-gray-800 pb-1">
            {renderInline(trimmed.slice(3))}
          </h2>
        );
      }
      if (trimmed.startsWith('# ')) {
        return (
          <h1 key={lineIdx} className="text-xl font-extrabold text-white mt-8 mb-4">
            {renderInline(trimmed.slice(2))}
          </h1>
        );
      }
      
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        return (
          <div key={lineIdx} className="flex items-start ml-4 my-1.5">
            <span className="text-emerald-500 mr-2 select-none">•</span>
            <span className="text-gray-300 leading-relaxed">{renderInline(trimmed.slice(2))}</span>
          </div>
        );
      }
      
      if (trimmed === '') {
        return <div key={lineIdx} className="h-2" />;
      }
      
      return (
        <p key={lineIdx} className="text-gray-300 my-2 leading-relaxed">
          {renderInline(line)}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-gray-100 px-4 py-6 md:p-8 relative matcher-main-container">
      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-0 left-0 -translate-y-1/4 -translate-x-1/4 w-[40%] h-[40%] rounded-full bg-indigo-600/20 blur-[120px]" />
        <div className="absolute bottom-0 right-0 translate-y-1/4 translate-x-1/4 w-[40%] h-[40%] rounded-full bg-purple-600/20 blur-[120px]" />
      </div>

      {/* Global Limit Modal for 0 credits clicks */}
      {showLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm">
          <PaywallModal 
            onClose={() => { setShowLimitModal(false); setInitialPlan(null); }} 
            onPaymentSuccess={handlePaymentSuccess}
            onServerVerifyPaypal={handleServerVerifyPaypal}
            initialPlanId={initialPlan}
            userId={session?.user?.id}
            userEmail={session?.user?.email}
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        <header className="mb-8">
          {/* Responsive Navigation Top Bar */}
          <div className="flex justify-between items-center py-4 mb-8 border-b border-white/5">
            <div className="flex items-center space-x-2 cursor-pointer animate-in fade-in duration-300" onClick={() => navigate('/')}>
              <div className="p-1.5 bg-indigo-500/20 rounded-lg border border-indigo-500/30 animate-pulse">
                <Sparkles className="w-4 h-4 text-indigo-400" />
              </div>
              <span className="text-base sm:text-lg font-bold tracking-tight text-white">CV Matcher<span className="text-indigo-400">Pro</span></span>
            </div>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => navigate('/interview')}
                className="text-xs sm:text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors mr-3"
              >
                AI Interview Coach →
              </button>
              
              {session ? (
                <>
                  <div className="hidden sm:flex items-center text-gray-400 bg-white/5 px-3 py-1.5 rounded-xl border border-white/10 text-xs font-medium">
                    <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" />
                    <span className="max-w-[150px] truncate">{session?.user?.email}</span>
                  </div>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center space-x-1.5 px-3.5 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl transition-all border border-gray-700 shadow-md text-xs font-medium"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="flex items-center space-x-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-all shadow-md text-xs md:text-sm"
                >
                  <User className="w-3.5 h-3.5" />
                  <span>Login / Register</span>
                </button>
              )}
            </div>
          </div>

          <div className="text-center px-5 my-6 md:my-10 animate-in fade-in duration-500">
            <h1 
              style={{ wordBreak: 'keep-all', overflow: 'visible' }}
              className="text-2xl sm:text-4xl md:text-5xl font-extrabold mb-4 tracking-tight text-white leading-tight"
            >
              Optimize your CV against the <span className="text-gradient">Job Description</span>
            </h1>
            <div className="mt-4 inline-flex items-center px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs sm:text-sm font-medium">
              {session ? `Credits Remaining: ${credits}` : '1 Free ATS Analysis Available'}
            </div>
          </div>

          <StepIndicator 
            currentStep={
              results ? 3 : (cvText.trim() && jobDescription.trim() ? 2.5 : (cvText.trim() ? 2 : 1))
            } 
          />
        </header>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* CV Input */}
          <div className="glass-panel p-6 flex flex-col relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center text-indigo-400">
                <FileText className="w-5 h-5 mr-2" />
                <h2 className="text-lg font-semibold text-white">Your CV / Resume</h2>
              </div>
              <label className="cursor-pointer group flex items-center px-3 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 rounded-lg transition-colors">
                {isPdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin text-indigo-400" /> : <Upload className="w-4 h-4 mr-2 text-indigo-400 group-hover:text-indigo-300" />}
                <span className="text-sm text-indigo-300 group-hover:text-indigo-200">Upload PDF</span>
                <input type="file" accept="application/pdf" className="hidden" onChange={handlePdfUpload} disabled={isPdfLoading} />
              </label>
            </div>
            <textarea
              value={cvText}
              onChange={(e) => setCvText(e.target.value)}
              placeholder="Paste your full CV text here..."
              className="flex-1 min-h-[300px] w-full bg-gray-950/50 border border-gray-800 rounded-xl p-4 text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all resize-none font-mono text-sm overflow-y-auto"
            />
          </div>

          {/* Job Description Input */}
          <div className="glass-panel p-6 flex flex-col">
            <div className="flex items-center mb-4 text-purple-400">
              <Briefcase className="w-5 h-5 mr-2" />
              <h2 className="text-lg font-semibold text-white">Job Description</h2>
            </div>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the job description here..."
              className="flex-1 min-h-[300px] w-full bg-gray-950/50 border border-gray-800 rounded-xl p-4 text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all resize-none font-mono text-sm overflow-y-auto"
            />
          </div>
        </div>

        <div className="flex flex-col items-center mb-12">
          <button
            onClick={handleAnalyze}
            disabled={loading}
            className="group relative px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden glow-effect"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="relative flex items-center">
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                  {session ? "Analyzing Match..." : "Analyzing CV..."}
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6 mr-3" />
                  {session ? "Analyze Match (1 Credit)" : "Analyze my CV for Free"}
                </>
              )}
            </span>
          </button>

          {loading && !session && (
            <div className="w-full max-w-md mt-6 bg-gray-900/50 border border-indigo-500/20 rounded-2xl p-6 text-center animate-pulse">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-4" />
              <h3 className="text-base font-semibold text-white mb-2">{simulationStep}</h3>
              <div className="w-full bg-gray-950 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded-full transition-all duration-700"
                  style={{
                    width: simulationStep.includes("structure") ? "33%" : (simulationStep.includes("keywords") ? "66%" : "95%")
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-start text-rose-400">
            <AlertCircle className="w-6 h-6 mr-3 shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {showSignupTeaser && !session && (
          <div className="mt-12 glass-panel p-6 md:p-10 relative overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-700">
            {/* Blurred background teaser results simulation */}
            <div className="absolute inset-0 bg-gray-950/80 backdrop-blur-md z-20 flex items-center justify-center p-3 sm:p-6">
              <div className="max-w-md w-full bg-gray-900 border border-indigo-500/30 px-4 py-6 md:p-8 rounded-3xl shadow-[0_0_50px_-10px_rgba(99,102,241,0.4)] text-center animate-in zoom-in-95 duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-500/20">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">🎯 Analysis Completed!</h3>
                <p className="text-gray-300 text-sm md:text-base mb-6 leading-relaxed">
                  We found critical keyword gaps and computed your ATS match score. Create a free account to unlock your report and tailored resume.
                </p>

                <div className="space-y-4">
                  <button
                    onClick={async () => {
                      sessionStorage.setItem('pending_cv_text', cvText);
                      sessionStorage.setItem('pending_job_desc', jobDescription);
                      sessionStorage.setItem('pending_trigger_analysis', 'true');
                      try {
                        const { error } = await supabase.auth.signInWithOAuth({
                          provider: 'google',
                          options: {
                            redirectTo: `${window.location.origin}/app`
                          }
                        });
                        if (error) throw error;
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                    className="w-full px-4 py-3.5 bg-white hover:bg-gray-100 text-gray-900 font-extrabold rounded-xl transition-all flex items-center justify-center shadow-lg text-sm md:text-base"
                  >
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5 mr-3" alt="Google" />
                    Unlock Free with Google
                  </button>

                  <button
                    onClick={() => {
                      sessionStorage.setItem('pending_cv_text', cvText);
                      sessionStorage.setItem('pending_job_desc', jobDescription);
                      sessionStorage.setItem('pending_trigger_analysis', 'true');
                      navigate('/login');
                    }}
                    className="w-full px-4 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold rounded-xl transition-all shadow-[0_0_20px_-5px_rgba(99,102,241,0.5)] flex items-center justify-center text-sm md:text-base"
                  >
                    Unlock Free with Email
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-6 flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-400" />
                  No credit card required • Instant access
                </p>
              </div>
            </div>

            {/* Simulated content behind the blur to trigger intense curiosity */}
            <div className="opacity-20 pointer-events-none select-none">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="glass-panel p-8 flex flex-col items-center justify-center text-center border-gray-800">
                  <div className="w-32 h-32 rounded-full border-4 border-gray-800 flex items-center justify-center mb-4">
                    <span className="text-3xl font-bold">??</span>
                  </div>
                  <div className="px-4 py-1.5 rounded-full bg-gray-800 text-gray-400 text-sm">Calculating...</div>
                </div>
                <div className="glass-panel p-6 md:col-span-2 flex flex-col justify-center border-gray-800">
                  <div className="h-4 bg-gray-800 rounded w-1/3 mb-4"></div>
                  <div className="h-3 bg-gray-800 rounded w-full mb-2"></div>
                  <div className="h-3 bg-gray-800 rounded w-5/6 mb-2"></div>
                  <div className="h-3 bg-gray-800 rounded w-4/5"></div>
                </div>
              </div>
              <div className="glass-panel p-6 border-gray-800">
                <div className="h-4 bg-gray-800 rounded w-1/4 mb-6"></div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-800 rounded w-1/2 mb-4"></div>
                    <div className="flex gap-2"><div className="h-8 bg-gray-800 rounded-lg w-20"></div><div className="h-8 bg-gray-800 rounded-lg w-24"></div></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-800 rounded w-1/2 mb-4"></div>
                    <div className="flex gap-2"><div className="h-8 bg-gray-800 rounded-lg w-24"></div><div className="h-8 bg-gray-800 rounded-lg w-20"></div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {results && !loading && (
          <div ref={resultsRef} className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 results-main-container">
            {/* Top Stats Row */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-panel p-8 flex flex-col items-center justify-center text-center">
                <div className="relative w-40 h-40 mb-4 flex items-center justify-center">
                  <svg className="w-full h-full transform -rotate-90 absolute inset-0" viewBox="0 0 36 36">
                    <path className="text-gray-800 stroke-current" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path className={`${getScoreColor(results.match_score)} transition-all duration-1000 ease-out`} strokeWidth="3" strokeDasharray={`${results.match_score}, 100`} fill="none" strokeLinecap="round" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-5xl font-bold ${getScoreColor(results.match_score)}`}>{results.match_score}</span>
                    <span className="text-gray-400 text-sm mt-1">/ 100</span>
                  </div>
                </div>
                <div className={`px-4 py-1.5 rounded-full border font-medium ${getScoreBg(results.match_score)}`}>{results.verdict}</div>
              </div>

              <div className="glass-panel p-6 md:col-span-2 flex flex-col justify-center">
                <h3 className="text-xl font-semibold mb-3 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-indigo-400" /> Executive Summary
                </h3>
                <p className="text-gray-300 leading-relaxed text-lg">{results.summary}</p>
              </div>
            </div>

            {/* Keywords */}
            <div className="glass-panel p-6">
              <h3 className="text-xl font-semibold mb-6 flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-400" /> Keyword Analysis
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-4 uppercase tracking-wider">Found Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {results.keywords_found?.map((kw, i) => <span key={i} className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 rounded-lg text-sm">{kw}</span>)}
                    {(!results.keywords_found || results.keywords_found.length === 0) && <span className="text-gray-500 text-sm italic">None found</span>}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-4 uppercase tracking-wider">Missing Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {results.keywords_missing?.map((kw, i) => <span key={i} className="px-3 py-1.5 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-lg text-sm">{kw}</span>)}
                    {(!results.keywords_missing || results.keywords_missing.length === 0) && <span className="text-gray-500 text-sm italic">None missing</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative mt-8">
              <div className="space-y-8 transition-all duration-500">
                <div className="relative">
                  {!premiumUnlocked ? (
                    <div className="glass-panel p-10 flex flex-col items-center justify-center text-center bg-indigo-950/20 border-indigo-500/30 backdrop-blur-md">
                      <div className="w-16 h-16 rounded-full bg-indigo-500/10 flex items-center justify-center mb-4">
                        <Lock className="w-8 h-8 text-indigo-400" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">Unlock Premium Insights</h3>
                      <p className="text-gray-400 mb-6 max-w-sm">
                        Get access to Detailed Strengths, Areas for Improvement, ATS Critical Warnings, and a Professional Summary rewrite.
                      </p>
                      <button
                        onClick={handleUnlockPremium}
                        disabled={isUnlockingPremium}
                        className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-all shadow-lg flex items-center"
                      >
                        {isUnlockingPremium ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Unlocking...</>
                        ) : (
                          <><Sparkles className="w-5 h-5 mr-2" /> Unlock Premium (1 Credit)</>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="glass-panel p-6">
                          <h3 className="text-xl font-semibold mb-6 flex items-center text-emerald-400">
                            <CheckCircle2 className="w-5 h-5 mr-2" /> Key Strengths
                          </h3>
                          <ul className="space-y-4">
                            {results.strengths?.map((strength, i) => (
                              <li key={i} className="flex items-start">
                                <div className="mt-1 mr-3 shrink-0 w-2 h-2 rounded-full bg-emerald-400" />
                                <div><strong className="block text-gray-200">{strength.point}</strong><span className="text-gray-400 text-sm">{strength.detail}</span></div>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="glass-panel p-6">
                          <h3 className="text-xl font-semibold mb-6 flex items-center text-indigo-400">
                            <ArrowRight className="w-5 h-5 mr-2" /> Areas for Improvement
                          </h3>
                          <ul className="space-y-4">
                            {results.improvements?.map((imp, i) => (
                              <li key={i} className="flex items-start">
                                <div className="mt-1 mr-3 shrink-0 w-2 h-2 rounded-full bg-indigo-400" />
                                <div><strong className="block text-gray-200">{imp.point}</strong><span className="text-gray-400 text-sm">{imp.detail}</span></div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {results.ats_warnings && results.ats_warnings.length > 0 && (
                        <div className="glass-panel p-6 border-orange-500/20 bg-orange-500/5 relative overflow-hidden">
                          <h3 className="text-xl font-semibold mb-4 flex items-center text-orange-400">
                            <AlertTriangle className="w-5 h-5 mr-2" /> ATS Warnings
                          </h3>
                          <div className="grid gap-3">
                            {results.ats_warnings.map((warning, i) => (
                              <div key={i} className="px-4 py-3 bg-orange-500/10 border border-orange-500/20 rounded-xl text-orange-200 flex items-start">
                                <AlertTriangle className="w-4 h-4 mr-3 shrink-0 mt-0.5 opacity-70" />
                                <p className="text-sm">{warning}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="glass-panel p-6 relative group">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                          <div className="flex items-center text-purple-400">
                            <Sparkles className="w-5 h-5 mr-2 shrink-0 animate-pulse" />
                            <h3 className="text-lg sm:text-xl font-bold text-white">Suggested Profile Summary</h3>
                          </div>
                          <button 
                            onClick={handleCopy} 
                            className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 rounded-xl transition-all text-xs sm:text-sm font-semibold shadow-md hover:shadow-purple-500/10 shrink-0"
                          >
                            {copied ? <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" /> : <Copy className="w-4 h-4 mr-2 text-purple-400" />}
                            <span>{copied ? 'Copied!' : 'Copy Summary'}</span>
                          </button>
                        </div>
                        <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5 text-gray-300 leading-relaxed font-serif italic text-base sm:text-lg">
                          "{results.rewritten_summary}"
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Optimized CV Preview Section */}
                <div id="optimized-cv-result" className="glass-panel p-6 border-indigo-500/30 bg-indigo-500/5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center text-indigo-400">
                      <Sparkles className="w-5 h-5 mr-2 shrink-0 animate-pulse" />
                      <h3 className="text-lg sm:text-xl font-bold text-white">Optimized CV Preview</h3>
                    </div>
                    <button 
                      onClick={handleCopyFullCV} 
                      className="w-full sm:w-auto flex items-center justify-center px-5 py-2.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 rounded-xl transition-all text-xs sm:text-sm font-bold shadow-md hover:shadow-indigo-500/10 group shrink-0"
                    >
                      {copiedFullCV ? <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" /> : <Copy className="w-4 h-4 mr-2 text-indigo-400" />}
                      <span>{copiedFullCV ? 'Copied!' : 'Copy Optimized CV'}</span>
                    </button>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Before */}
                    <div className="flex flex-col">
                      <div className="flex items-center mb-3 text-gray-500 text-xs font-bold uppercase tracking-widest">
                        <span className="w-2 h-2 rounded-full bg-gray-600 mr-2" /> Original Version
                      </div>
                      <div className="flex-1 bg-gray-950/40 border border-gray-800 rounded-2xl p-5 text-gray-500 text-sm font-mono whitespace-pre-wrap">
                        {cvText}
                      </div>
                    </div>

                    {/* After */}
                    <div className="flex flex-col">
                      <div className="flex items-center mb-3 text-emerald-500 text-xs font-bold uppercase tracking-widest">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse mr-2" /> Optimized Version
                      </div>
                      <div className="flex-1 bg-gray-950/80 border border-emerald-500/20 rounded-2xl p-5 text-gray-200 text-sm font-sans">
                        {renderMarkdown(results.optimized_cv, results.keywords_missing)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl flex items-start text-indigo-300 text-sm">
                    <Sparkles className="w-5 h-5 mr-3 shrink-0 text-indigo-400" />
                    <p>
                      We've automatically incorporated your <strong>missing keywords</strong> into this optimized version. 
                      You can copy the text on the right and use it to update your resume.
                    </p>
                  </div>
                </div>

                {/* Interview Coach Call to Action */}
                <div className="glass-panel p-8 border-purple-500/30 bg-purple-500/5 text-center flex flex-col items-center justify-center relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-[80px] rounded-full pointer-events-none" />
                  <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-purple-400 animate-pulse" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Ready to land this job?</h3>
                  <p className="text-gray-400 mb-6 max-w-lg">
                    Practice a realistic mock interview tailored specifically to this job description. Test your technical and behavioral skills with AI feedback.
                  </p>
                  <button
                    onClick={() => navigate('/interview')}
                    className="flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold rounded-xl transition-all shadow-[0_0_20px_-5px_rgba(168,85,247,0.5)] scale-100 hover:scale-[1.02] active:scale-95 duration-200"
                  >
                    <span>Now practice your interview for this job →</span>
                  </button>
                </div>

              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Matcher;
